import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, RefreshCw, Loader2, CheckCircle2, XCircle, Search } from 'lucide-react';
import { startLeadSync, getLeadSyncProgress } from '@/services/api/meta-leads';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import type { LeadSyncProgressResponse } from '@/types/meta-lead';

interface SyncLeadsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pages: MetaPage[];
    onSyncComplete: () => void;
    initialSelectedPageId?: number;
    onBackgroundSync?: (taskId: string) => void;
}

const FOLDER_LABELS: Record<string, string> = {
    inbox: 'Caixa de Entrada',
    page_done: 'Concluídas',
    spam: 'Spam',
    other: 'Outras',
};

export default function SyncLeadsDialog({
    open,
    onOpenChange,
    pages,
    onSyncComplete,
    initialSelectedPageId,
    onBackgroundSync,
}: SyncLeadsDialogProps) {
    const [selectedPageIds, setSelectedPageIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
    const [progress, setProgress] = useState<LeadSyncProgressResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const activePages = pages.filter((p) => p.is_active);
    const isSinglePageMode = !!initialSelectedPageId;

    const normalize = (str: string) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const filteredPages = useMemo(() => {
        let list = activePages;
        if (isSinglePageMode) {
            list = activePages.filter((p) => p.id === initialSelectedPageId);
        }
        if (!searchTerm.trim()) return list;
        const term = normalize(searchTerm);
        return list.filter(
            (p) => normalize(p.page_name).includes(term) || p.page_id.includes(searchTerm.trim())
        );
    }, [activePages, searchTerm, isSinglePageMode, initialSelectedPageId]);

    // Cleanup polling on unmount or close
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const resetState = useCallback(() => {
        setSelectedPageIds([]);
        setSelectAll(true);
        setIsSyncing(false);
        setActiveTaskId(null);
        setProgress(null);
        setError(null);
        setSearchTerm('');
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    // Set initial selected page if provided
    useEffect(() => {
        if (open && initialSelectedPageId) {
            setSelectAll(false);
            setSelectedPageIds([initialSelectedPageId]);
        } else if (open && !initialSelectedPageId) {
            setSelectAll(true);
            setSelectedPageIds([]);
        }
    }, [open, initialSelectedPageId]);

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            if (progress?.status === 'completed' || progress?.status === 'failed' || !isSyncing) {
                resetState();
                onOpenChange(false);
            }
        } else {
            onOpenChange(true);
        }
    };

    const handlePageToggle = (pageId: number) => {
        setSelectAll(false);
        setSelectedPageIds((prev) =>
            prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]
        );
    };

    const handleSelectAll = () => {
        setSelectAll(true);
        setSelectedPageIds([]);
    };

    const pollProgress = useCallback(
        (tid: string) => {
            intervalRef.current = setInterval(async () => {
                try {
                    const data = await getLeadSyncProgress(tid);

                    if (data.status === 'not_found') {
                        setProgress({ ...data, status: 'completed' });
                    } else {
                        setProgress(data);
                    }

                    if (data.status === 'completed' || data.status === 'failed' || data.status === 'not_found') {
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                        setIsSyncing(false);

                        if (data.status === 'completed' || data.status === 'not_found') {
                            onSyncComplete();
                        }
                    }
                } catch {
                    // Silently retry on next interval
                }
            }, 2000);
        },
        [onSyncComplete]
    );

    const handleStartSync = async () => {
        setError(null);
        setIsSyncing(true);

        try {
            const pageIds = selectAll ? [] : selectedPageIds;
            const result = await startLeadSync(pageIds);
            setActiveTaskId(result.task_id);
            setProgress({
                task_id: result.task_id,
                status: 'queued',
                current_page: '',
                current_page_index: 0,
                total_pages: 0,
                total_conversations: 0,
                leads_created: 0,
                leads_existing: 0,
                leads_failed: 0,
                current_folder: '',
                error: '',
            });
            pollProgress(result.task_id);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Erro ao iniciar sincronização');
            setIsSyncing(false);
        }
    };

    const handleClose = () => {
        resetState();
        onOpenChange(false);
    };

    const isCompleted = progress?.status === 'completed';
    const isFailed = progress?.status === 'failed';
    const isRunning = isSyncing && !isCompleted && !isFailed;

    // Calculate progress percentage
    const progressPercent =
        progress && progress.total_pages > 0
            ? Math.round((progress.current_page_index / progress.total_pages) * 100)
            : 0;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        Sincronizar Leads
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        {!isSyncing && !progress
                            ? isSinglePageMode
                                ? 'Sincronize os leads desta página.'
                                : 'Importe leads das conversas do Messenger.'
                            : isRunning
                                ? 'Sincronizando leads...'
                                : isCompleted
                                    ? 'Sincronização concluída!'
                                    : 'Erro na sincronização'}
                    </DialogDescription>
                </DialogHeader>

                {/* State 1: Page Selection */}
                {!isSyncing && !progress && (
                    <div className="space-y-4 py-4">
                        {/* Selection summary */}
                        {!isSinglePageMode && (selectAll || selectedPageIds.length > 0) && (
                            <div className="inline-flex items-center gap-2 bg-[#7dd3fc]/10 text-[#0f172a] px-3 py-1.5 rounded-full text-sm font-medium">
                                <Check className="h-4 w-4" />
                                {selectAll
                                    ? `${activePages.length} páginas (todas)`
                                    : `${selectedPageIds.length} página${selectedPageIds.length > 1 ? 's' : ''} selecionada${selectedPageIds.length > 1 ? 's' : ''}`}
                            </div>
                        )}

                        {/* Search + Actions */}
                        {!isSinglePageMode && (
                            <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        type="text"
                                        autoComplete="off"
                                        placeholder="Buscar por nome ou ID..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={selectAll ? () => { setSelectAll(false); setSelectedPageIds([]); } : handleSelectAll}
                                    className="whitespace-nowrap h-9 text-xs"
                                >
                                    {selectAll ? 'Desmarcar Todas' : 'Selecionar Todas'}
                                </Button>
                            </div>
                        )}

                        {/* Page List */}
                        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                            {filteredPages.length === 0 ? (
                                <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg">
                                    {activePages.length === 0
                                        ? 'Nenhuma página ativa encontrada.'
                                        : 'Nenhuma página corresponde à busca.'}
                                </div>
                            ) : (
                                filteredPages.map((page) => {
                                    const selected = selectAll || selectedPageIds.includes(page.id);
                                    return (
                                        <button
                                            key={page.id}
                                            type="button"
                                            onClick={() => !isSinglePageMode && handlePageToggle(page.id)}
                                            className={`relative w-full p-3 border-2 rounded-xl text-left transition-all hover:border-[#7dd3fc] hover:shadow-sm ${
                                                selected
                                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-sm'
                                                    : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                                        selected
                                                            ? 'bg-[#0f172a] border-[#0f172a]'
                                                            : 'border-gray-300 bg-white'
                                                    }`}
                                                >
                                                    {selected && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="w-10 h-10 bg-[#0f172a] rounded-full flex items-center justify-center">
                                                        <span className="text-white font-semibold text-xs">
                                                            {page.page_name.substring(0, 2).toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium text-sm text-gray-900 truncate">
                                                        {page.page_name}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        ID: {page.page_id}
                                                    </p>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {error && (
                            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {/* State 2: Progress */}
                {(isRunning || isCompleted || isFailed) && progress && (
                    <div className="space-y-4 py-4">
                        {/* Progress Bar */}
                        <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>
                                    {progress.current_page_index > 0
                                        ? `Página ${progress.current_page_index} de ${progress.total_pages}`
                                        : 'Iniciando...'}
                                </span>
                                <span>{progressPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                    className={`h-2.5 rounded-full transition-all duration-500 ${isFailed
                                        ? 'bg-red-500'
                                        : isCompleted
                                            ? 'bg-green-500'
                                            : 'bg-[#0f172a]'
                                        }`}
                                    style={{ width: `${isCompleted ? 100 : progressPercent}%` }}
                                />
                            </div>
                        </div>

                        {/* Current Status */}
                        {isRunning && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Loader2 className="h-4 w-4 animate-spin text-[#0f172a]" />
                                <span>
                                    {progress.current_page && (
                                        <>
                                            <strong>{progress.current_page}</strong>
                                            {progress.current_folder && (
                                                <> &middot; {FOLDER_LABELS[progress.current_folder] || progress.current_folder}</>
                                            )}
                                        </>
                                    )}
                                    {!progress.current_page && 'Preparando...'}
                                </span>
                            </div>
                        )}

                        {/* Success Icon */}
                        {isCompleted && (
                            <div className="flex items-center gap-2 text-sm text-green-700">
                                <CheckCircle2 className="h-5 w-5" />
                                <span className="font-medium">Sincronização concluída!</span>
                            </div>
                        )}

                        {/* Error Icon */}
                        {isFailed && (
                            <div className="flex items-center gap-2 text-sm text-red-700">
                                <XCircle className="h-5 w-5" />
                                <span className="font-medium">
                                    {progress.error || 'Erro durante a sincronização'}
                                </span>
                            </div>
                        )}

                        {/* Counters */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-lg border border-gray-200 p-3 text-center">
                                <div className="text-2xl font-bold text-[#0f172a]">
                                    {progress.leads_created}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Novos leads</div>
                            </div>
                            <div className="rounded-lg border border-gray-200 p-3 text-center">
                                <div className="text-2xl font-bold text-gray-600">
                                    {progress.leads_existing}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Já existentes</div>
                            </div>
                            <div className="rounded-lg border border-gray-200 p-3 text-center">
                                <div className="text-2xl font-bold text-gray-400">
                                    {progress.total_conversations}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Conversas</div>
                            </div>
                        </div>

                        {progress.leads_failed > 0 && (
                            <div className="text-xs text-red-500 text-center">
                                {progress.leads_failed} falha(s) ao processar
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                    {/* State 1: Selection buttons */}
                    {!isSyncing && !progress && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                className="border-gray-300"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleStartSync}
                                disabled={!selectAll && selectedPageIds.length === 0}
                                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sincronizar
                            </Button>
                        </>
                    )}

                    {/* State 2: Running */}
                    {isRunning && (
                        <div className="flex w-full justify-between items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    if (activeTaskId) {
                                        onBackgroundSync?.(activeTaskId);
                                        handleClose();
                                    }
                                }}
                                className="border-gray-300"
                            >
                                Sincronizar em Background
                            </Button>
                            <Button disabled className="bg-[#0f172a] text-white opacity-70">
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sincronizando...
                            </Button>
                        </div>
                    )}

                    {/* State 3: Completed / Failed */}
                    {(isCompleted || isFailed) && (
                        <Button
                            onClick={handleClose}
                            className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                        >
                            Fechar
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
